import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Mail, User, ArrowLeft, CheckCircle2 } from 'lucide-react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

interface RegisterProps {
  onBackToLogin: () => void;
}

type RuoloRichiesto = 'videomaker' | 'video_editor';

interface FormErrors {
  first_name?: string;
  last_name?: string;
  username?: string;
  email?: string;
  password?: string;
  confirm?: string;
}

const inputStyle = {
  backgroundColor: '#F4F5F7',
  color: '#13161C',
  borderColor: '#DDE0E6',
};

const labelClass = 'block text-xs font-medium uppercase tracking-[0.12em] mb-2';
const labelStyle = { color: '#5B6270' };

const Register: React.FC<RegisterProps> = ({ onBackToLogin }) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [ruolo, setRuolo] = useState<RuoloRichiesto>('videomaker');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const [inviata, setInviata] = useState(false);

  const validate = (): boolean => {
    const e: FormErrors = {};

    if (!firstName.trim()) e.first_name = 'Inserisci il tuo nome';
    if (!lastName.trim()) e.last_name = 'Inserisci il tuo cognome';

    if (!username.trim()) {
      e.username = 'Scegli un nome utente';
    } else if (!/^[a-zA-Z0-9_]{3,50}$/.test(username)) {
      e.username = 'Da 3 a 50 caratteri: lettere, numeri e underscore';
    }

    if (!email.trim()) {
      e.email = 'Inserisci la tua email';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      e.email = 'Email non valida';
    }

    if (!password) {
      e.password = 'Scegli una password';
    } else if (password.length < 8) {
      e.password = 'Almeno 8 caratteri';
    } else if (!/[a-z]/.test(password) || !/[A-Z]/.test(password) || !/[0-9]/.test(password)) {
      e.password = 'Deve contenere maiuscole, minuscole e numeri';
    }

    if (confirm !== password) e.confirm = 'Le password non coincidono';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();

    if (!validate()) {
      toast.error('Controlla i dati inseriti');
      return;
    }

    setSubmitting(true);

    try {
      const response = await fetch(`${API_BASE_URL}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: firstName.trim(),
          last_name: lastName.trim(),
          username: username.trim(),
          email: email.trim().toLowerCase(),
          password,
          role: ruolo,
        }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setInviata(true);
        return;
      }

      // Errori di validazione del server (lista) o messaggio singolo
      const dettaglio = Array.isArray(data.errors) && data.errors.length > 0
        ? data.errors[0].msg || data.errors[0].message
        : data.message;

      toast.error(dettaglio || 'Registrazione non riuscita');
    } catch (error) {
      console.error('Registrazione fallita:', error);
      toast.error('Impossibile contattare il server. Riprova.');
    } finally {
      setSubmitting(false);
    }
  };

  if (inviata) {
    return (
      <div
        className="min-h-screen min-h-[100dvh] flex items-center justify-center p-4 sm:p-6 md:p-8"
        style={{ backgroundColor: '#FAFAFB' }}
      >
        <motion.div
          className="w-full max-w-[340px] sm:max-w-sm md:max-w-md text-center"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <div
            className="p-7 sm:p-8 rounded-[10px]"
            style={{
              backgroundColor: '#FFFFFF',
              border: '1px solid #EBEDF1',
              boxShadow: 'none',
            }}
          >
            <CheckCircle2
              className="h-12 w-12 mx-auto"
              style={{ color: '#4ade80' }}
              strokeWidth={1.5}
            />

            <h2
              className="mt-5 text-xl sm:text-2xl font-medium"
              style={{
                color: '#13161C',
                letterSpacing: '-0.02em',
                fontFamily: "'Libre Baskerville', Georgia, serif",
              fontWeight: 400,
              }}
            >
              Richiesta inviata
            </h2>

            <p className="mt-3 text-sm leading-relaxed" style={{ color: '#5B6270' }}>
              Il tuo account è stato creato ed è in attesa di approvazione.
              Un amministratore lo attiverà e ti assegnerà i clienti su cui lavorare:
              riceverai l'accesso subito dopo.
            </p>

            <button
              type="button"
              onClick={onBackToLogin}
              className="w-full mt-7 py-3.5 text-sm sm:text-base font-semibold rounded-md touch-manipulation active:scale-[0.98] transition-all duration-200 hover:-translate-y-px"
              style={{
                backgroundColor: '#FDF751',
                color: '#13161C',
                boxShadow: 'none',
              }}
            >
              Torna all'accesso
            </button>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div
      className="min-h-screen min-h-[100dvh] flex items-center justify-center p-4 sm:p-6 md:p-8 relative overflow-hidden"
      style={{ backgroundColor: '#FAFAFB' }}
    >
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at top, rgba(19,22,28,0.03) 0%, transparent 60%)',
        }}
        aria-hidden="true"
      />

      <motion.div
        className="w-full max-w-[340px] sm:max-w-sm md:max-w-md relative py-8"
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="text-center mb-7 sm:mb-8">
          <img
            src="/bymarke-wordmark-white.png"
            alt="ByMarke"
            className="h-7 sm:h-8 w-auto mx-auto select-none"
            style={{ filter: 'brightness(0)' }}
            draggable={false}
          />

          <h2
            className="mt-6 text-2xl sm:text-3xl font-medium"
            style={{
              color: '#13161C',
              letterSpacing: '-0.02em',
              fontFamily: "'Libre Baskerville', Georgia, serif",
              fontWeight: 400,
            }}
          >
            Crea il tuo account
          </h2>

          <p className="mt-2 text-sm" style={{ color: '#5B6270' }}>
            Un amministratore approverà la richiesta
          </p>
        </div>

        <div
          className="p-6 sm:p-7 md:p-8 rounded-[10px]"
          style={{
            backgroundColor: '#FFFFFF',
            border: '1px solid #EBEDF1',
            boxShadow: 'none',
          }}
        >
          <form className="space-y-4" onSubmit={handleSubmit}>
            {/* Nome e cognome */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="first_name" className={labelClass} style={labelStyle}>
                  Nome
                </label>
                <input
                  id="first_name"
                  type="text"
                  autoComplete="given-name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="w-full px-3 py-3 text-sm rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="Mario"
                />
                {errors.first_name && (
                  <p className="mt-1 text-xs text-red-400">{errors.first_name}</p>
                )}
              </div>

              <div>
                <label htmlFor="last_name" className={labelClass} style={labelStyle}>
                  Cognome
                </label>
                <input
                  id="last_name"
                  type="text"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="w-full px-3 py-3 text-sm rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="Rossi"
                />
                {errors.last_name && (
                  <p className="mt-1 text-xs text-red-400">{errors.last_name}</p>
                )}
              </div>
            </div>

            {/* Username */}
            <div>
              <label htmlFor="username" className={labelClass} style={labelStyle}>
                Nome utente
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <User className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                </div>
                <input
                  id="username"
                  type="text"
                  autoComplete="username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  className="w-full pl-11 sm:pl-12 pr-3 py-3.5 text-sm sm:text-base rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="mario_rossi"
                />
              </div>
              {errors.username && (
                <p className="mt-1.5 text-xs text-red-400">{errors.username}</p>
              )}
            </div>

            {/* Email */}
            <div>
              <label htmlFor="reg_email" className={labelClass} style={labelStyle}>
                Email
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                </div>
                <input
                  id="reg_email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full pl-11 sm:pl-12 pr-3 py-3.5 text-sm sm:text-base rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="mario@esempio.com"
                />
              </div>
              {errors.email && <p className="mt-1.5 text-xs text-red-400">{errors.email}</p>}
            </div>

            {/* Ruolo */}
            <div>
              <span className={labelClass} style={labelStyle}>
                Di cosa ti occupi
              </span>
              <div className="grid grid-cols-2 gap-2">
                {([
                  { value: 'videomaker', label: 'Videomaker' },
                  { value: 'video_editor', label: 'Video Editor' },
                ] as Array<{ value: RuoloRichiesto; label: string }>).map((opt) => {
                  const attivo = ruolo === opt.value;
                  return (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setRuolo(opt.value)}
                      className="py-3 px-2 text-sm rounded-md border transition-all duration-200 touch-manipulation active:scale-[0.98]"
                      style={{
                        backgroundColor: attivo ? '#FDF751' : '#F4F5F7',
                        borderColor: attivo ? '#FDF751' : '#DDE0E6',
                        color: attivo ? '#13161C' : '#5B6270',
                        fontWeight: attivo ? 600 : 400,
                      }}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Password */}
            <div>
              <label htmlFor="reg_password" className={labelClass} style={labelStyle}>
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                </div>
                <input
                  id="reg_password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-11 sm:pl-12 pr-11 sm:pr-12 py-3.5 text-sm sm:text-base rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="Almeno 8 caratteri"
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-3 sm:pr-4 flex items-center touch-manipulation"
                  onClick={() => setShowPassword(!showPassword)}
                  aria-label={showPassword ? 'Nascondi password' : 'Mostra password'}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                  ) : (
                    <Eye className="h-4 w-4 sm:h-5 sm:w-5 text-slate-400" />
                  )}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1.5 text-xs text-red-400">{errors.password}</p>
              )}
            </div>

            {/* Conferma password */}
            <div>
              <label htmlFor="confirm" className={labelClass} style={labelStyle}>
                Conferma password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 sm:pl-4 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 sm:h-5 sm:w-5 text-slate-500" />
                </div>
                <input
                  id="confirm"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="w-full pl-11 sm:pl-12 pr-3 py-3.5 text-sm sm:text-base rounded-md border focus:outline-none transition-all duration-200 focus:border-[#13161C]"
                  style={inputStyle}
                  placeholder="Ripeti la password"
                />
              </div>
              {errors.confirm && <p className="mt-1.5 text-xs text-red-400">{errors.confirm}</p>}
            </div>

            <button
              type="submit"
              disabled={submitting}
              className="w-full py-3.5 mt-2 text-sm sm:text-base font-semibold rounded-md disabled:opacity-50 disabled:cursor-not-allowed touch-manipulation active:scale-[0.98] transition-all duration-200 hover:-translate-y-px"
              style={{
                backgroundColor: '#FDF751',
                color: '#13161C',
                boxShadow: 'none',
              }}
            >
              {submitting ? 'Invio in corso...' : 'Richiedi accesso'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={onBackToLogin}
              className="inline-flex items-center gap-1.5 text-xs transition-opacity hover:opacity-80"
              style={{ color: '#5B6270' }}
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Ho già un account
            </button>
          </div>
        </div>

        <div className="mt-8 text-center">
          <p
            className="text-[10px] uppercase tracking-[0.2em] font-medium"
            style={{ color: '#8B92A0' }}
          >
            ByMarke · Social Dashboard
          </p>
        </div>
      </motion.div>
    </div>
  );
};

export default Register;
